import { StateIDs } from '@config/state-id';
import { isString, SBaseDisplay } from '@summit/core';
import { SummitFormattedText, SummitIdObject } from '@summit/models';

import { Address } from '../address';
import { State } from '../state/state';
import { HubParameters } from './hub-parameters.enum';

export class Hub extends SummitIdObject implements SBaseDisplay {
  static AdminScheduleDisplayString = '0?';
  override id: number;
  get idString(): string {
    return this.id.toString();
  }
  name: string;
  abbreviation: string;
  parameters: HubParameters;
  stateID: string;
  state: State;
  addressId?: string;
  address: Address;
  acquireOverlapping: boolean;

  region: string;
  district: string;
  typeName: string;
  phoneNumber: string;
  faxNumber: string;

  //hack for typing
  get hidden(): boolean {
    return this.HasParameter(HubParameters.Hidden);
  }
  set hidden(value: boolean) {
    this.SetParameter(HubParameters.Hidden, value);
  }

  //cache
  _addressFormatted: SummitFormattedText;

  constructor(jsonData?: Partial<Hub>) {
    super(jsonData);
    //console.log(`Creating Hub from ${JSON.stringify(jsonData, null, 2)}`);
    if (jsonData) {
      // console.log(`Got raw data of ${JSON.stringify(jsonData, null, 2)}`)
      if (jsonData.state) {
        this.state = new State(jsonData.state);
      }
      if (jsonData.address) {
        this.address = new Address(jsonData.address);
      }
      // console.log(`Created ${JSON.stringify(this, null, 2)} from ${JSON.stringify(jsonData, null, 2)}`)
    }
    if (!this.stateID || this.stateID.length === 0) {
      // console.log(`Adding default state to ${this.toShortString()}`);
      this.stateID = StateIDs.UnknownState;
    }
    this.address ??= new Address();
    this.addressId ??= this.address.id;
    this.acquireOverlapping ??= false;
    if (isString(this.id)) this.id = Number.parseInt(this.id);
  }

  get hubNameDisplay(): string {
    return this.id.toString() === this.name ? this.id.toString() : `${this.name} (${this.id})`;
  }

  get hubNameString(): string {
    return this.id?.toString() === this.name ? this.id?.toString() : `${this.id} - ${this.name}`;
  }

  get boundaryLocked(): boolean {
    return this.HasParameter(HubParameters.BoundaryLocked);
  }
  set boundaryLocked(newValue: boolean) {
    this.SetParameter(HubParameters.BoundaryLocked, newValue);
  }

  get invalid(): boolean {
    return this.HasParameter(HubParameters.Invalid);
  }
  set invalid(newValue: boolean) {
    this.SetParameter(HubParameters.Invalid, newValue);
  }

  get stateAbbreviation(): string {
    return this.state?.abbreviation;
  }

  get addressFormatted(): SummitFormattedText {
    if (this._addressFormatted !== undefined) return this._addressFormatted;
    if (this.address) {
      this._addressFormatted = new SummitFormattedText();
      this._addressFormatted.AddLine(this.address.ToMailingFormat().toString());
    } else {
      this._addressFormatted = null;
    }
    return this._addressFormatted;
  }
  set addressFormatted(newValue: SummitFormattedText) {
    /*NOP*/
  }

  toShortString(): string {
    if (!this.id && this.id !== 0) {
      return 'New';
    }
    if (this.id && this.name && this.id.toString() !== this.name) {
      return this.id.toString() === this.name ? this.id.toString() : `${this.id} - ${this.name}`;
    }
    return this.id.toString() === this.name ? this.id.toString() : `${this.name}`;
  }

  HasParameter(flag: HubParameters): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.parameters & flag);
  }

  SetParameter(flag: HubParameters, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.parameters |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.parameters &= ~flag;
    }
  }
}
